import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSpacetimeDB, useTable } from 'spacetimedb/react';
import { tables as orgTables, type DbConnection as OrgConn } from '@sdk/org';
import { tables as minetwinTables } from '@sdk/minetwin';
import type { Site } from '@/data/types';
import { useRole } from '@/hooks/useRole';
import { awaitProcedureJson } from '@/utils/awaitProcedure';
import { mergeAdminSiteIds, siteSourceFor } from '@/lib/siteList';

/**
 * Live list of Sites (= Projects in backend terminology) from the org module.
 *
 * Returns rows mapped onto the prototype's existing `Site` shape so the
 * Topbar site-picker, sidebar site-card, and App.tsx routing don't need to
 * change. `seats` / `active` are placeholders (0) until we wire org-side
 * member counts — out of scope for MVP-1.
 *
 * Two source paths (mirrors the TurboTwin webclient's `useVisibleProjects`):
 *
 *   - **Org admin / viewer** → the `admin_list_projects` procedure. The org
 *     `Project` table is private + scoped by the `my_projects` view, which by
 *     design does NOT serve org admins: post-Phase-B4 an org admin holds no
 *     `ProjectUser` row, so the view returns an empty/partial list and the site
 *     picker would show "No sites assigned". A view cannot full-scan, so admins
 *     read the complete list through the procedure (full scan behind the
 *     OrgAdmin/OrgViewer gate). Must be a WS call (`getConnection()`), not HTTP.
 *   - **Orientation admin** → `my_projects` merged with the minetwin
 *     `admin_site_ids` view (ORIENTATION-ROLES P5). TurboTwin sees them as a
 *     Member, so the procedure would refuse them.
 *   - **Everyone else (members)** → the reactive `my_projects` view via
 *     `useTable('org', orgTables.my_projects)`, exactly as before.
 *
 * Note on database-name resolution: per the TurboTwin SDK docs clients SHOULD
 * source per-project Spacetime database names from
 * `org.module_deployment.databaseName` rather than string-constructing
 * `{org}-{project}-core`. The portal currently does the string-construction
 * (see SpacetimeProvider.moduleNameFor) because the live `module_deployment`
 * table is sparsely populated.
 */

/** Shape of one project in the `admin_list_projects` procedure JSON (snake_case). */
interface AdminProjectJson {
  id: number;
  project_name: string;
  display_name: string;
  status: string; // Debug repr of the enum tag, e.g. "Active"
}

function toSiteFromAdmin(p: AdminProjectJson): Site {
  return {
    id: p.id.toString(),
    name: p.display_name,
    projectName: p.project_name,
    abbr: p.project_name.slice(0, 2).toUpperCase(),
    seats: 0,
    active: 0,
  };
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any -- generated `project` view row
function toSiteFromView(p: any): Site {
  return {
    id: p.id.toString(),
    name: p.displayName,
    projectName: p.projectName,
    abbr: p.projectName.slice(0, 2).toUpperCase(),
    seats: 0,
    active: 0,
  };
}

export function useProjects(): { sites: Site[]; isReady: boolean } {
  const source = siteSourceFor(useRole());
  // OrgAdmin and OrgViewer both need the procedure path (`admin_list_projects`
  // is gated on OrgAdmin/OrgViewer); members fall through to the reactive view.
  const useAdminPath = source === 'procedure';
  // Orientation admin: the minetwin admin-scope site ids (called
  // unconditionally, hook rules).
  const [adminSiteIds, adminIdsReady] = useTable('minetwin', minetwinTables.admin_site_ids);

  // Member path: reactive `my_projects` view. Called unconditionally (hook
  // rules); ignored when on the admin path.
  const [projects, viewReady] = useTable('org', orgTables.my_projects);
  const memberSites = useMemo<Site[]>(
    () =>
      projects
        .filter((p) => p.status?.tag === 'Active')
        .slice()
        .sort((a, b) => a.displayName.localeCompare(b.displayName))
        .map(toSiteFromView),
    [projects],
  );

  // Admin path: one-shot procedure fetch over the live org WS connection.
  const orgState = useSpacetimeDB('org');
  const orgActive = orgState.isActive;
  const [adminSites, setAdminSites] = useState<Site[]>([]);
  const [fetchedOnce, setFetchedOnce] = useState(false);
  const [adminFailed, setAdminFailed] = useState(false);

  const fetchAdmin = useCallback(async () => {
    const conn = orgState.getConnection() as OrgConn | null;
    if (!conn) return;
    try {
      const data = await awaitProcedureJson<{ projects: AdminProjectJson[] }>(
        conn,
        // eslint-disable-next-line @typescript-eslint/no-explicit-any -- generated procedures accessor
        (c: any) => c.procedures.adminListProjects({}),
      );
      const sites = (data.projects ?? [])
        .filter((p) => p.status === 'Active')
        .slice()
        .sort((a, b) => a.display_name.localeCompare(b.display_name))
        .map(toSiteFromAdmin);
      setAdminSites(sites);
      setAdminFailed(false);
    } catch (err) {
      // Fall back to the member view (e.g. `admin_user` not yet synced) so the
      // user still sees any public projects rather than a hard-empty list.
      console.warn('useProjects: admin_list_projects failed, falling back to view', err);
      setAdminFailed(true);
    } finally {
      setFetchedOnce(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- orgState identity churns; gate on isActive
  }, [orgActive]);

  useEffect(() => {
    if (useAdminPath && orgActive) void fetchAdmin();
  }, [useAdminPath, orgActive, fetchAdmin]);

  if (source === 'view+admin-sites') {
    return {
      sites: mergeAdminSiteIds(memberSites, adminSiteIds),
      isReady: viewReady && adminIdsReady,
    };
  }
  if (!useAdminPath) {
    return { sites: memberSites, isReady: viewReady };
  }
  // On the admin path, fall back to the member view if the procedure failed.
  if (adminFailed) {
    return { sites: memberSites, isReady: fetchedOnce && viewReady };
  }
  return { sites: adminSites, isReady: fetchedOnce };
}
